import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { logger } from '@common';
import { getSocialMediaThumbnail, ContentTypeEnum } from '../utils/getSocialMediaThumbnail';

interface UseSocialMediaThumbnailResult {
  imageUrl: string | null;
  isLoading: boolean;
  error: string | null;
  contentType: ContentTypeEnum | null;
  title?: string;
  onImageError: () => void;
}

export type HookProps = {
  postUrl: string;
  postImageUrl?: string | null;
};

/**
 * A custom hook that handles fetching social media thumbnails with fallback and error handling.
 * It will first try to use the provided postImageUrl if available, and fall back to fetching
 * a thumbnail from the social media service if the postImageUrl is not provided or fails to load.
 */
export const useSocialMediaThumbnail = (props: HookProps): UseSocialMediaThumbnailResult => {
  const { postUrl, postImageUrl } = props;

  // Define refs to track state across renders
  const lastErroredUrl = useRef<string | null>(null);
  const postImageUrlFailed = useRef(false);
  const isMounted = useRef(true);
  const currentUrlRef = useRef(postUrl);

  // State for loading, thumbnail URL, content type, error, and title
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contentType, setContentType] = useState<ContentTypeEnum | null>(null);
  const [title, setTitle] = useState<string | undefined>(undefined);
  const [shouldFetch, setShouldFetch] = useState(false);

  // Update current URL ref when postUrl changes
  useEffect(() => {
    currentUrlRef.current = postUrl;
  }, [postUrl]);

  // Set up the unmount cleanup
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Handle postImageUrl
  useEffect(() => {
    if (postImageUrl && !postImageUrlFailed.current) {
      logger.info('[Thumbnail] Using provided postImageUrl for', postUrl);
      setThumbnailUrl(postImageUrl);
      setContentType(ContentTypeEnum.IMAGE);
      setIsLoading(false);
      setShouldFetch(false);
    }
  }, [postUrl, postImageUrl]);

  // Reset error state when URL changes
  useEffect(() => {
    if (lastErroredUrl.current !== postUrl) {
      logger.info('[Thumbnail] Resetting error state for new URL', postUrl);
      setError(null);
      lastErroredUrl.current = null;
      postImageUrlFailed.current = false;
      setShouldFetch(!postImageUrl);

      if (!postImageUrl) {
        setIsLoading(true);
      }
    }
  }, [postUrl, postImageUrl]);

  // Fetch thumbnail
  useEffect(() => {
    // Skip if we have a working postImageUrl
    if (postImageUrl && !postImageUrlFailed.current && !shouldFetch) {
      logger.info('[Thumbnail] Using provided postImageUrl for', postUrl);
      return;
    }

    // Don't retry failed thumbnails for the same URL
    if (lastErroredUrl.current === postUrl && !postImageUrlFailed.current) {
      logger.info('[Thumbnail] Not retrying failed thumbnail for', postUrl);
      return;
    }

    logger.info('[Thumbnail] Fetching thumbnail for', postUrl);

    const fetchThumbnail = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Store the URL we're fetching for - important for race conditions
        const fetchingForUrl = postUrl;

        const response = await getSocialMediaThumbnail(fetchingForUrl);

        if (!isMounted.current) return;

        // For concurrent URL changes test: check if URL changed during fetch
        if (currentUrlRef.current !== fetchingForUrl) {
          logger.info('[Thumbnail] URL changed during fetch, ignoring result for', fetchingForUrl);
          return;
        }

        if (response.error) {
          logger.error('[Thumbnail] Error fetching thumbnail:', response.error);
          lastErroredUrl.current = fetchingForUrl;
          setError(response.error);
        } else {
          logger.info('[Thumbnail] Successfully fetched thumbnail for', fetchingForUrl);
          setThumbnailUrl(response.thumbnailUrl);
          setContentType(response.contentType);
          setTitle(response.title);
          lastErroredUrl.current = null;
        }
      } catch (err: unknown) {
        logger.error('[Thumbnail] Error fetching thumbnail:', err);
        if (!isMounted.current) return;

        // Don't update state if URL changed during fetch
        if (currentUrlRef.current !== postUrl) {
          return;
        }

        lastErroredUrl.current = postUrl;
        setError('Failed to fetch thumbnail');
      } finally {
        if (isMounted.current) {
          setIsLoading(false);
          setShouldFetch(false);
        }
      }
    };

    fetchThumbnail();
  }, [postUrl, postImageUrl, shouldFetch]);

  // Handle image load errors
  const onImageError = useCallback(() => {
    // If this was a postImageUrl failure, trigger a thumbnail fetch
    if (thumbnailUrl === postImageUrl) {
      logger.info('[Thumbnail] postImageUrl failed, will try fetching thumbnail');
      postImageUrlFailed.current = true;
      setThumbnailUrl(null);
      setError(null);
      setShouldFetch(true);
      return;
    }

    // For thumbnail failures
    if (lastErroredUrl.current === postUrl) {
      return;
    }

    lastErroredUrl.current = postUrl;
    setError('Failed to load image');
    setThumbnailUrl(null);
  }, [postUrl, postImageUrl, thumbnailUrl]);

  return useMemo(
    () => ({
      imageUrl: thumbnailUrl,
      isLoading,
      error,
      contentType,
      title,
      onImageError,
    }),
    [thumbnailUrl, isLoading, error, contentType, title, onImageError],
  );
};
